/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import type { Product } from '../../../catalog/domain-api'
import type { ProductVariant } from '../../../catalog/domain-api';
import { buildVariantGridLayout } from '../../../catalog/domain-api';
import { getAvailability } from '../../../inventory/stock/infra/operations/getAvailability';
import type { PosStockSalePolicy } from '../../domain/posTypes';
import { colors } from '../../../shared/theme/colors'
import { spacing } from '../../../shared/theme/spacing'
import { typography } from '../../../shared/theme/typography';
import { formatPosMoney } from '../../application/posMoneyFormat';
import { ModalShell } from '../../../shared/modals/ModalShell'
import { modalExperience } from '../../../shared/modals/modalExperience.styles';
import { posSharedUi as posUi } from '../../../shared/ui/posSharedUi';

type Props = {
  visible: boolean;
  product: Product | null;
  variants: ProductVariant[];
  branchId?: string | null;
  stockSalePolicy?: PosStockSalePolicy;
  onCancel: () => void;
  onSelect: (variant: ProductVariant) => void;
};

function variantLabel(variant: ProductVariant): string {
  const suffix = variant.nameSuffix?.trim();
  if (suffix) return suffix;
  const attrs = Object.entries(variant.attributes ?? {});
  if (attrs.length > 0) {
    return attrs.map(([, v]) => v).join(' · ');
  }
  return 'Variante';
}

type VariantRowState = {
  variant: ProductVariant;
  sellable?: number;
  blocked: boolean;
  label: string;
  price: number;
};

export function PosVariantPickerModal({
  visible,
  product,
  variants,
  branchId,
  stockSalePolicy = 'allow',
  onCancel,
  onSelect,
}: Props) {
  const [sellableByVariantId, setSellableByVariantId] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!visible || !product || !branchId) {
      setSellableByVariantId({});
      return;
    }
    let cancelled = false;
    void (async () => {
      const results = await Promise.all(
        variants.map((variant) => getAvailability(branchId, product.id, variant.id)),
      );
      if (cancelled) return;
      const next: Record<string, number> = {};
      variants.forEach((variant, i) => {
        next[variant.id] = results[i].sellable;
      });
      setSellableByVariantId(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, product, branchId, variants]);

  const rows = useMemo<VariantRowState[]>(
    () =>
      variants.map((variant) => {
        const sellable = sellableByVariantId[variant.id];
        const blocked = stockSalePolicy === 'block' && sellable != null && sellable <= 0;
        return {
          variant,
          sellable,
          blocked,
          label: variantLabel(variant),
          price: variant.price ?? product?.price ?? 0,
        };
      }),
    [variants, sellableByVariantId, stockSalePolicy, product?.price],
  );

  const grid = useMemo(() => buildVariantGridLayout(variants), [variants]);
  const rowByVariantId = useMemo(() => new Map(rows.map((row) => [row.variant.id, row])), [rows]);

  const renderCell = (rowState: VariantRowState) => {
    const stockHint =
      rowState.sellable != null ? (rowState.sellable > 0 ? `${rowState.sellable}` : '0') : '—';
    // M2: Show a visual hint when the variant has no own price and falls back to the parent.
    const hasOwnPrice = rowState.variant.price != null && rowState.variant.price > 0;
    return (
      <TouchableOpacity
        key={rowState.variant.id}
        style={[styles.gridCell, rowState.blocked ? styles.gridCellDisabled : null]}
        onPress={() => {
          if (rowState.blocked) return;
          onSelect(rowState.variant);
        }}
        disabled={rowState.blocked}
        accessibilityRole="button"
        accessibilityState={{ disabled: rowState.blocked }}
        accessibilityLabel={`${rowState.label}, stock ${stockHint}${!hasOwnPrice ? ', usa precio del producto' : ''}`}
      >
        <Text style={[styles.gridCellStock, rowState.blocked ? styles.gridCellTextDisabled : null]}>
          {stockHint}
        </Text>
        <Text style={[styles.gridCellPrice, rowState.blocked ? styles.gridCellTextDisabled : null]}>
          {formatPosMoney(rowState.price)}
          {!hasOwnPrice ? ' *' : ''}
        </Text>
      </TouchableOpacity>
    );
  };

  return (
    <ModalShell
      visible={visible}
      onClose={onCancel}
      title="Elegí variante"
      subtitle={product?.name}
      keyboardAvoiding={false}
      widthVariant="compact"
      footer={
        <TouchableOpacity
          style={[posUi.secondaryButton, modalExperience.footerActions]}
          onPress={onCancel}
          accessibilityRole="button"
          accessibilityLabel="Cancelar selección de variante"
        >
          <Text style={posUi.secondaryButtonText}>Cancelar</Text>
        </TouchableOpacity>
      }
    >
      {grid ? (
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          <View style={styles.gridWrap}>
            <View style={styles.gridHeaderRow}>
              <View style={styles.gridCorner} />
              {grid.cols.map((col) => (
                <View key={col} style={styles.gridHeaderCell}>
                  <Text style={styles.gridHeaderText}>{col}</Text>
                </View>
              ))}
            </View>
            {grid.rows.map((color) => (
              <View key={color} style={styles.gridBodyRow}>
                <View style={styles.gridRowLabel}>
                  <Text style={styles.gridRowLabelText}>{color}</Text>
                </View>
                {grid.cols.map((talle) => {
                  const variant = grid.cellByKey.get(`${color}|${talle}`);
                  if (!variant) {
                    return <View key={`${color}-${talle}`} style={styles.gridCellEmpty} />;
                  }
                  const rowState = rowByVariantId.get(variant.id);
                  if (!rowState) return <View key={`${color}-${talle}`} style={styles.gridCellEmpty} />;
                  return renderCell(rowState);
                })}
              </View>
            ))}
          </View>
        </ScrollView>
      ) : (
        <View style={styles.list}>
          {rows.map((rowState) => {
            const stockHint =
              rowState.sellable != null
                ? rowState.sellable > 0
                  ? `Stock: ${rowState.sellable}`
                  : 'Sin stock'
                : null;
            return (
              <TouchableOpacity
                key={rowState.variant.id}
                style={[styles.variantRow, rowState.blocked ? styles.variantRowDisabled : null]}
                onPress={() => {
                  if (rowState.blocked) return;
                  onSelect(rowState.variant);
                }}
                disabled={rowState.blocked}
                accessibilityRole="button"
                accessibilityState={{ disabled: rowState.blocked }}
                accessibilityLabel={`Variante: ${rowState.label}, ${formatPosMoney(rowState.price)}${stockHint ? `, ${stockHint}` : ''}`}
              >
                <View style={styles.variantMain}>
                  <Text style={[styles.variantName, rowState.blocked ? styles.variantNameDisabled : null]}>
                    {rowState.label}
                  </Text>
                  {stockHint ? (
                    <Text style={[styles.variantStock, rowState.blocked ? styles.variantStockDisabled : null]}>
                      {stockHint}
                    </Text>
                  ) : null}
                </View>
                <Text style={[styles.variantPrice, rowState.blocked ? styles.variantPriceDisabled : null]}>
                  {formatPosMoney(rowState.price)}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>
      )}
    </ModalShell>
  );
}

const styles = StyleSheet.create({
  list: { gap: spacing.xs },
  variantRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.borderSoft,
    borderRadius: 10,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    backgroundColor: colors.inputBackground,
  },
  variantRowDisabled: {
    opacity: 0.55,
  },
  variantMain: { flex: 1, gap: 2 },
  variantName: { ...typography.body, color: colors.textPrimary },
  variantNameDisabled: { color: colors.textSecondary },
  variantStock: { ...typography.caption, color: colors.textSecondary },
  variantStockDisabled: { color: colors.textSecondary },
  variantPrice: { ...typography.body, color: colors.accent, fontWeight: '700' },
  variantPriceDisabled: { color: colors.textSecondary },
  gridWrap: { gap: spacing.xs },
  gridHeaderRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
  gridCorner: { width: 72 },
  gridHeaderCell: {
    width: 72,
    alignItems: 'center',
    paddingVertical: spacing.xs,
  },
  gridHeaderText: { ...typography.caption, color: colors.textSecondary, fontWeight: '700' },
  gridBodyRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
  gridRowLabel: { width: 72, paddingRight: spacing.xs },
  gridRowLabelText: { ...typography.caption, color: colors.textPrimary, fontWeight: '600' },
  gridCell: {
    width: 72,
    minHeight: 56,
    borderWidth: 1,
    borderColor: colors.borderSoft,
    borderRadius: 10,
    padding: spacing.xs,
    backgroundColor: colors.inputBackground,
    justifyContent: 'space-between',
  },
  gridCellDisabled: { opacity: 0.55 },
  gridCellEmpty: { width: 72, minHeight: 56 },
  gridCellStock: { ...typography.caption, color: colors.textPrimary, fontWeight: '700', textAlign: 'center' },
  gridCellPrice: { ...typography.caption, color: colors.accent, textAlign: 'center' },
  gridCellTextDisabled: { color: colors.textSecondary },
});
